import dayjs from 'dayjs';
import React, { useContext, useMemo, useState } from 'react';

/**
 * Right Sidebar, Slide out menus and etc..
 */
export enum SideBarStates {
    Closed = 'Closed',
    Profile = 'Profile',
    Notifications = 'Notifications',
    Grades = 'Grades',
    Chat = 'Chat',
    Comments = 'Comments',
    RSSFeed = 'RSSFeed',
    Agenda = 'Agenda',
}

type SidebarContextType = {
    open: boolean;
    state: SideBarStates;
    agendaDate?: dayjs.Dayjs;
};

type SidebarActionsType = {
    toggle: (nextState: SideBarStates, date?: dayjs.Dayjs) => void;
};

const SidebarContext = React.createContext<SidebarContextType | null>(null);
const SidebarDispatchContext = React.createContext<SidebarActionsType | null>(null);

export function SidebarContextProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<SidebarContextType>({
        open: false,
        state: SideBarStates.Closed,
        agendaDate: dayjs(),
    });

    const actions = useMemo(
        () => ({
            toggle(nextState: SideBarStates = SideBarStates.Closed, date: dayjs.Dayjs = dayjs()) {
                setState((s) => {
                    const nxtState = nextState === s.state ? SideBarStates.Closed : nextState;
                    return {
                        open: nxtState !== SideBarStates.Closed,
                        state: nxtState,
                        agendaDate: date,
                    };
                });
            },
        }),
        [setState],
    );

    return (
        <SidebarContext.Provider value={state}>
            <SidebarDispatchContext.Provider value={actions}>
                {children}
            </SidebarDispatchContext.Provider>
        </SidebarContext.Provider>
    );
}

export function useSidebarState() {
    const context = useContext(SidebarContext);
    if (context === null) {
        throw new Error('Expected Context for <Sidebar /> to be Initialized');
    }

    return context;
}

export function useSidebarDispatch() {
    const context = useContext(SidebarDispatchContext);
    if (context === null) {
        throw new Error('Expected Dispatch Context for <Sidebar /> to be Initialized');
    }

    return context;
}
